import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { X, MessageSquare, Reply, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

export interface FloatingMessageNotification {
  id: string;
  messageId: string;
  senderId: string;
  senderName: string;
  senderPhoto?: string;
  content: string;
  timestamp: Date;
  seen?: boolean;
  conversationId?: string;
  isTyping?: boolean;
}

interface FloatingMessageNotificationProps {
  notification: FloatingMessageNotification;
  position: number;
  onClose: (id: string) => void;
  onReply: (senderId: string) => void;
  onMarkAsRead: (id: string) => void;
  onOpenChat: (senderId: string) => void;
}

export function FloatingMessageNotificationToast({
  notification,
  position,
  onClose,
  onReply,
  onMarkAsRead,
  onOpenChat
}: FloatingMessageNotificationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const [isHovered, setIsHovered] = useState(false);

  const DISPLAY_DURATION = 8000; // 8 seconds
  const bottomOffset = 24 + (position * 120); // Stack notifications

  useEffect(() => {
    // Animate in
    const showTimer = setTimeout(() => setIsVisible(true), 100);
    
    // Auto dismiss
    const progressTimer = setInterval(() => {
      if (!isHovered) {
        setProgress(prev => {
          const newProgress = prev - (100 / (DISPLAY_DURATION / 100));
          if (newProgress <= 0) {
            onClose(notification.id);
            return 0;
          }
          return newProgress;
        });
      }
    }, 100);

    return () => {
      clearTimeout(showTimer);
      clearInterval(progressTimer);
    };
  }, [isHovered, notification.id, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(notification.id), 300);
  };

  const handleReply = () => {
    onReply(notification.senderId);
    handleClose();
  };

  const handleOpenChat = () => {
    onOpenChat(notification.senderId);
    handleClose();
  };

  const handleMarkAsRead = () => {
    onMarkAsRead(notification.id);
    handleClose();
  };

  const truncatedContent = notification.content.length > 80 
    ? `${notification.content.substring(0, 80)}...` 
    : notification.content;

  return (
    <div
      className={cn(
        "fixed right-6 z-50 transition-all duration-500 ease-out",
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      )}
      style={{ bottom: `${bottomOffset}px` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Card className="w-80 bg-white shadow-2xl border-0 overflow-hidden rounded-2xl relative group">
        {/* Progress bar */}
        <div className="absolute top-0 left-0 h-1 bg-gradient-to-r from-blue-500 via-blue-600 to-cyan-500 transition-all duration-100 ease-linear"
             style={{ width: `${progress}%` }} />

        {/* Glow effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-blue-500/10 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="p-4 relative">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Avatar className="h-10 w-10 ring-2 ring-white shadow-md">
                  <AvatarImage src={notification.senderPhoto} />
                  <AvatarFallback className="bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold">
                    {notification.senderName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                
                {/* Online indicator */}
                <div className="absolute -top-1 -right-1 h-4 w-4 bg-white rounded-full flex items-center justify-center">
                  <MessageSquare className="h-2.5 w-2.5 text-blue-500" />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-900 text-sm truncate">
                    {notification.senderName}
                  </p>
                  {!notification.seen && (
                    <Badge className="bg-blue-500 text-white text-xs h-4 px-1.5 rounded-full">
                      New
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                </p>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          {/* Message content */}
          <div className="mb-4">
            {notification.isTyping ? (
              <div className="flex items-center gap-2 text-blue-500">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <p className="text-sm italic">is typing...</p>
              </div>
            ) : (
              <p className="text-gray-700 text-sm leading-relaxed bg-gray-50 rounded-lg p-3 border-l-4 border-blue-500">
                {truncatedContent}
              </p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAsRead}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-3 py-1.5 h-auto text-xs rounded-full"
            >
              <Eye className="h-3 w-3 mr-1" />
              Mark as read
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenChat}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-1.5 h-auto text-xs rounded-full"
            >
              <MessageSquare className="h-3 w-3 mr-1" />
              Open
            </Button>
            
            <Button
              size="sm"
              onClick={handleReply}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-1.5 h-auto text-xs rounded-full shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <Reply className="h-3 w-3 mr-1" />
              Reply
            </Button>
          </div>
        </div>

        {/* Bottom accent */}
        <div className="h-1 bg-gradient-to-r from-blue-500 via-blue-600 to-cyan-500 opacity-20" />
      </Card>
    </div>
  );
}